import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { TablerIcon } from "@/components/icons/TablerIcon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  loadCompanyPrefs,
  saveCompanyPrefs,
  type CompanyPrefsV1,
} from "@/data/companyPrefs";
import type { FinishStandard, VatMode } from "@/types/mvpEstimate";
import { WIZARD_REGION_OPTIONS } from "@/wizard/mvpWizardScopeCatalog";
import { cn } from "@/lib/utils";

type SettingsTab = "firma" | "oferty" | "powiadomienia";

const VAT_MODES: { id: VatMode; label: string }[] = [
  { id: "none", label: "Bez VAT" },
  { id: "8", label: "8%" },
  { id: "23", label: "23%" },
];

const STANDARDS: { value: FinishStandard; label: string }[] = [
  { value: "ekonomiczny", label: "Ekonomiczny" },
  { value: "standard", label: "Standard" },
  { value: "premium", label: "Premium" },
];

const TABS: { id: SettingsTab; label: string; icon: Parameters<typeof TablerIcon>[0]["name"] }[] = [
  { id: "firma", label: "Firma", icon: "building" },
  { id: "oferty", label: "Oferty", icon: "receipt" },
  { id: "powiadomienia", label: "Powiadomienia", icon: "bell" },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [tab, setTab] = useState<SettingsTab>("firma");
  const [loading, setLoading] = useState(false);
  const profileLogoInputRef = useRef<HTMLInputElement>(null);
  const [company, setCompany] = useState<CompanyPrefsV1>(() => loadCompanyPrefs());
  const [companyProfile, setCompanyProfile] = useState({
    company_name: "",
    company_nip: "",
    company_address: "",
    company_phone: "",
    company_email: "",
    company_logo_url: "" as string,
  });
  const [companyLogoPreviewUrl, setCompanyLogoPreviewUrl] = useState<string | null>(null);
  const [companyLogoBusy, setCompanyLogoBusy] = useState(false);
  const [notifications, setNotifications] = useState({
    notification_email: "",
    notify_offer_accepted: true,
    notify_offer_viewed: false,
    notify_weekly_summary: true,
  });

  useEffect(() => {
    setCompany(loadCompanyPrefs());
  }, [user]);

  useEffect(() => {
    const userId = user?.id;
    if (!userId) return;
    let cancelled = false;

    const run = async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select(
          "company_name, company_nip, company_address, company_phone, company_email, company_logo_url, notification_email, notify_offer_accepted, notify_offer_viewed, notify_weekly_summary",
        )
        .eq("id", userId)
        .single();
      if (cancelled || error || !data) return;

      const row = data as Record<string, unknown>;
      setCompanyProfile({
        company_name: (row.company_name as string) ?? "",
        company_nip: (row.company_nip as string) ?? "",
        company_address: (row.company_address as string) ?? "",
        company_phone: (row.company_phone as string) ?? "",
        company_email: (row.company_email as string) ?? "",
        company_logo_url: (row.company_logo_url as string) ?? "",
      });
      setNotifications({
        notification_email: (row.notification_email as string) ?? (row.company_email as string) ?? "",
        notify_offer_accepted: row.notify_offer_accepted !== false,
        notify_offer_viewed: row.notify_offer_viewed === true,
        notify_weekly_summary: row.notify_weekly_summary !== false,
      });
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    let cancelled = false;
    const path = companyProfile.company_logo_url?.trim();
    if (!user || !path) {
      setCompanyLogoPreviewUrl(null);
      return;
    }
    supabase.storage
      .from("company-logos")
      .createSignedUrl(path, 60 * 60)
      .then(({ data, error }) => {
        if (cancelled) return;
        setCompanyLogoPreviewUrl(error || !data?.signedUrl ? null : data.signedUrl);
      });
    return () => {
      cancelled = true;
    };
  }, [user, companyProfile.company_logo_url]);

  const setCompanyField = <K extends keyof CompanyPrefsV1>(key: K, val: CompanyPrefsV1[K]) =>
    setCompany((p) => ({ ...p, [key]: val }));

  const setCompanyProfileField = <K extends keyof typeof companyProfile>(
    key: K,
    val: (typeof companyProfile)[K],
  ) => setCompanyProfile((p) => ({ ...p, [key]: val }));

  const nipDigits = companyProfile.company_nip.replace(/\D/g, "");
  const nipWarning = nipDigits.length > 0 && nipDigits.length !== 10;

  const handleUploadCompanyLogo = async (fileList: FileList | null) => {
    const file = fileList?.[0];
    if (!user || !file) return;
    const allowed = ["image/png", "image/jpeg", "image/svg+xml"];
    if (!allowed.includes(file.type)) {
      toast({ title: "Nieprawidłowy format", description: "Dozwolone: PNG, JPG, SVG.", variant: "destructive" });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast({ title: "Plik za duży", description: "Maksymalny rozmiar logo to 2MB.", variant: "destructive" });
      return;
    }

    setCompanyLogoBusy(true);
    try {
      const ext = file.type === "image/png" ? "png" : file.type === "image/jpeg" ? "jpg" : "svg";
      const path = `${user.id}/${crypto.randomUUID()}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from("company-logos")
        .upload(path, file, { upsert: false, contentType: file.type });
      if (uploadError) {
        toast({ title: "Błąd uploadu", description: uploadError.message, variant: "destructive" });
        return;
      }
      const prevPath = companyProfile.company_logo_url?.trim();
      if (prevPath) await supabase.storage.from("company-logos").remove([prevPath]);
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ company_logo_url: path })
        .eq("id", user.id);
      if (updateError) {
        toast({ title: "Błąd zapisu", description: updateError.message, variant: "destructive" });
        return;
      }
      setCompanyProfileField("company_logo_url", path);
      toast({ title: "Logo wgrane" });
    } finally {
      setCompanyLogoBusy(false);
      if (profileLogoInputRef.current) profileLogoInputRef.current.value = "";
    }
  };

  const handleDeleteCompanyLogo = async () => {
    if (!user) return;
    const path = companyProfile.company_logo_url?.trim();
    if (!path) return;
    setCompanyLogoBusy(true);
    try {
      await supabase.storage.from("company-logos").remove([path]);
      const { error } = await supabase.from("profiles").update({ company_logo_url: null }).eq("id", user.id);
      if (error) {
        toast({ title: "Błąd", description: error.message, variant: "destructive" });
        return;
      }
      setCompanyProfileField("company_logo_url", "");
      setCompanyLogoPreviewUrl(null);
      toast({ title: "Usunięto logo" });
    } finally {
      setCompanyLogoBusy(false);
    }
  };

  const handleSaveFirma = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setLoading(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        company_name: companyProfile.company_name.trim() || null,
        company_nip: companyProfile.company_nip.trim() || null,
        company_address: companyProfile.company_address.trim() || null,
        company_phone: companyProfile.company_phone.trim() || null,
        company_email: companyProfile.company_email.trim() || null,
      })
      .eq("id", user.id);
    setLoading(false);
    if (error) {
      toast({ title: "Błąd", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Zapisano dane firmy" });
  };

  const handleSaveOferty = (e: React.FormEvent) => {
    e.preventDefault();
    saveCompanyPrefs({ ...loadCompanyPrefs(), ...company, version: 1 });
    toast({ title: "Zapisano ustawienia ofert" });
  };

  const handleSaveNotifications = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setLoading(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        notification_email: notifications.notification_email.trim() || null,
        notify_offer_accepted: notifications.notify_offer_accepted,
        notify_offer_viewed: notifications.notify_offer_viewed,
        notify_weekly_summary: notifications.notify_weekly_summary,
      })
      .eq("id", user.id);
    setLoading(false);
    if (error) {
      toast({ title: "Błąd", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Zapisano preferencje powiadomień" });
  };

  return (
    <DashboardLayout>
      <header className="dashboard-page-header">
        <div>
          <h1 className="dashboard-page-title">Ustawienia</h1>
          <p className="dashboard-subtitle">Dane firmy, domyślne parametry ofert i powiadomienia.</p>
        </div>
      </header>

      <div className="settings-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={cn("settings-tab", tab === t.id && "is-active")}
            onClick={() => setTab(t.id)}
          >
            <TablerIcon name={t.icon} size={16} />
            {t.label}
          </button>
        ))}
      </div>

      <div className="settings-panel">
        {tab === "firma" && (
          <form onSubmit={(e) => void handleSaveFirma(e)} className="settings-card space-y-4">
            <div className="settings-field">
              <Label htmlFor="co-name">Nazwa firmy</Label>
              <Input
                id="co-name"
                value={companyProfile.company_name}
                onChange={(e) => setCompanyProfileField("company_name", e.target.value)}
                placeholder="np. Jan Kowalski Remonty Sp. z o.o."
                className="h-11"
              />
            </div>
            <div className="settings-field">
              <Label htmlFor="co-nip">NIP</Label>
              <Input
                id="co-nip"
                value={companyProfile.company_nip}
                onChange={(e) => setCompanyProfileField("company_nip", e.target.value)}
                placeholder="1234567890"
                className="h-11 font-mono"
              />
              {nipWarning && (
                <p className="mt-1 text-xs text-[var(--ochre)]">NIP powinien mieć 10 cyfr.</p>
              )}
            </div>
            <div className="settings-field">
              <Label htmlFor="co-address">Adres</Label>
              <Textarea
                id="co-address"
                value={companyProfile.company_address}
                onChange={(e) => setCompanyProfileField("company_address", e.target.value)}
                rows={2}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="settings-field">
                <Label htmlFor="co-phone">Telefon</Label>
                <Input
                  id="co-phone"
                  value={companyProfile.company_phone}
                  onChange={(e) => setCompanyProfileField("company_phone", e.target.value)}
                  className="h-11"
                />
              </div>
              <div className="settings-field">
                <Label htmlFor="co-email">E-mail</Label>
                <Input
                  id="co-email"
                  type="email"
                  value={companyProfile.company_email}
                  onChange={(e) => setCompanyProfileField("company_email", e.target.value)}
                  className="h-11"
                />
              </div>
            </div>
            <div className="settings-field">
              <Label>Logo</Label>
              <input
                ref={profileLogoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                className="sr-only"
                onChange={(e) => void handleUploadCompanyLogo(e.target.files)}
              />
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={companyLogoBusy}
                  onClick={() => profileLogoInputRef.current?.click()}
                >
                  {companyLogoBusy ? "Wgrywam…" : "Wybierz plik"}
                </Button>
                {companyLogoPreviewUrl && (
                  <>
                    <img src={companyLogoPreviewUrl} alt="" className="h-12 w-12 rounded-lg border object-contain" />
                    <button
                      type="button"
                      className="text-sm text-[var(--ink-3)] underline"
                      onClick={() => void handleDeleteCompanyLogo()}
                    >
                      Usuń
                    </button>
                  </>
                )}
              </div>
            </div>
            <Button type="submit" className="dashboard-btn-primary border-0" disabled={loading}>
              {loading ? "Zapisuję…" : "Zapisz"}
            </Button>
          </form>
        )}

        {tab === "oferty" && (
          <form onSubmit={handleSaveOferty} className="settings-card space-y-6">
            <div className="settings-field">
              <Label htmlFor="validity">Domyślna ważność oferty</Label>
              <select
                id="validity"
                className="flex h-11 w-full max-w-xs rounded-md border border-input bg-background px-3 text-sm"
                value={company.defaultValidityDays ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "") setCompanyField("defaultValidityDays", undefined);
                  else if (v === "14" || v === "21" || v === "30")
                    setCompanyField("defaultValidityDays", v);
                }}
              >
                <option value="">— bez ustawienia —</option>
                <option value="14">14 dni</option>
                <option value="21">21 dni</option>
                <option value="30">30 dni</option>
              </select>
            </div>
            <div className="settings-field">
              <Label>Domyślny VAT</Label>
              <div className="flex flex-wrap gap-2">
                {VAT_MODES.map(({ id, label }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setCompanyField("vatMode", id)}
                    className={cn(
                      "rounded-lg border px-3 py-2 text-sm font-semibold",
                      company.vatMode === id
                        ? "border-[var(--ochre)] bg-[var(--ochre-soft)] text-[var(--ochre-deep)]"
                        : "border-[var(--line)]",
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="settings-field">
              <Label>Standard wykończenia</Label>
              <div className="grid grid-cols-3 gap-2">
                {STANDARDS.map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setCompanyField("standard", value)}
                    className={cn(
                      "rounded-lg border px-3 py-2.5 text-sm font-semibold",
                      company.standard === value
                        ? "border-[var(--ochre)] bg-[var(--ochre-soft)] text-[var(--ochre-deep)]"
                        : "border-[var(--line)]",
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="settings-field">
              <Label>Strefa cenowa</Label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {WIZARD_REGION_OPTIONS.map(({ id, label }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setCompanyField("region", id)}
                    className={cn(
                      "rounded-lg border px-3 py-2 text-sm font-semibold",
                      company.region === id
                        ? "border-[var(--ochre)] bg-[var(--ochre-soft)] text-[var(--ochre-deep)]"
                        : "border-[var(--line)]",
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <Button type="submit" className="dashboard-btn-primary border-0">
              Zapisz
            </Button>
          </form>
        )}

        {tab === "powiadomienia" && (
          <form onSubmit={(e) => void handleSaveNotifications(e)} className="settings-card space-y-2">
            <div className="settings-field pb-4">
              <Label htmlFor="notify-email">E-mail do powiadomień</Label>
              <Input
                id="notify-email"
                type="email"
                value={notifications.notification_email}
                onChange={(e) =>
                  setNotifications((p) => ({ ...p, notification_email: e.target.value }))
                }
                placeholder="np. biuro@firma.pl"
                className="h-11"
              />
            </div>
            <div className="settings-toggle-row">
              <div>
                <p className="text-sm font-semibold text-[var(--ink)]">Akceptacja oferty</p>
                <p className="text-xs text-[var(--ink-3)]">Gdy klient zaakceptuje ofertę online.</p>
              </div>
              <Switch
                checked={notifications.notify_offer_accepted}
                onCheckedChange={(v) =>
                  setNotifications((p) => ({ ...p, notify_offer_accepted: v }))
                }
              />
            </div>
            <div className="settings-toggle-row">
              <div>
                <p className="text-sm font-semibold text-[var(--ink)]">Otwarcie oferty</p>
                <p className="text-xs text-[var(--ink-3)]">Gdy klient otworzy link do oferty.</p>
              </div>
              <Switch
                checked={notifications.notify_offer_viewed}
                onCheckedChange={(v) =>
                  setNotifications((p) => ({ ...p, notify_offer_viewed: v }))
                }
              />
            </div>
            <div className="settings-toggle-row">
              <div>
                <p className="text-sm font-semibold text-[var(--ink)]">Podsumowanie tygodnia</p>
                <p className="text-xs text-[var(--ink-3)]">Krótki raport aktywności co tydzień.</p>
              </div>
              <Switch
                checked={notifications.notify_weekly_summary}
                onCheckedChange={(v) =>
                  setNotifications((p) => ({ ...p, notify_weekly_summary: v }))
                }
              />
            </div>
            <Button type="submit" className="dashboard-btn-primary mt-4 border-0" disabled={loading}>
              {loading ? "Zapisuję…" : "Zapisz"}
            </Button>
          </form>
        )}
      </div>
    </DashboardLayout>
  );
}
